import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
    fetchData,
    findMenuWithParent,
    formatDate,
    getEncryptedRoute,
    toasterrormsg,
    toastsuccessmsg,
    useMenuIdPermissions,
} from '../../../reusable'
import Swal from 'sweetalert2'
import { KTIcon } from '../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import DataTable from 'react-data-table-component'
import Loader from '../components/Loader'
import { Tr } from '../../../_metronic/i18n/Tr'
import { PostReportMenu } from '../components/css/PostReportMenu'

const PostManagement: FC = () => {
    let t = Tr()
    const { menuId, menuIdEdit, menuIdDelete, menuIdView } = useMenuIdPermissions()
    const tmp = sessionStorage.getItem('admin')
    const adminId = tmp ? JSON.parse(tmp)?.adminId ?? '' : ''

    const [postList, setPostList] = useState([])
    const [selectedStatus, setSelectedStatus] = useState("PENDING")
    const [searchTerm, setSearchTerm] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [totalRecords, setTotalRecords] = useState(0)
    const [limit, setLimit] = useState(10)
    const [offset, setOffset] = useState(0)
    const [sortField, setSortField] = useState('')
    const [sortOrder, setSortOrder] = useState('')
    const [isDataLoaded, setIsDataLoaded] = useState(false)
    const [focus, setFocus] = useState('')


    const postListAPIcall = async () => {
        try {
            if (!isDataLoaded) {
                setIsLoading(true)
            }
            const param = {
                limit: limit,
                offset: offset,
                status: selectedStatus,
                term: searchTerm.trim(),
                sortBy: sortField,
                sort: sortOrder
            }
            const response = await fetchData('admin/explore/post/status/list', param)

            if (response.status == '200') {
                const data = response.data
                setTotalRecords(data.totalRecord || 0)
                setPostList(data.list || [])
                setIsDataLoaded(true)
            } else {
                toasterrormsg(response.message)
                setPostList([])
                setTotalRecords(0)
            }
        } catch (error) {
            console.error('Error fetching post list:', error)
            toasterrormsg('Failed to fetch posts')
            setPostList([])
            setTotalRecords(0)
        } finally {
            setIsLoading(false)
        }
    }

    const approveRejectPost = async (action: 'APPROVE' | 'REJECT', postId: number, postedBy: string) => {
        let swalOptions: any = {
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }

        if (action === 'REJECT') {
            swalOptions = {
                ...swalOptions,
                title: t('Are you sure you want to reject this post?'),
                input: 'text',
                inputLabel: t('Reason'),
                inputPlaceholder: t('Enter reject reason'),
                inputValidator: (value: string) => {
                    if (!value) return t('Reject reason is required')
                },
            }
        }
        if (action === 'APPROVE') {
            swalOptions.title = t('Are you sure you want to approve this post?')
        }

        const result = await Swal.fire(swalOptions)
        if (!result.isConfirmed) return

        const param: any = {
            postId,
            status: action,
            adminId,
            postedBy,
        }
        if (action == "REJECT") {
            param.reason = result.value
        }

        try {
            setIsLoading(true)
            const response = await fetchData('admin/explore/post/status', param)

            if (String(response?.status) === '200') {
                postListAPIcall()
                toastsuccessmsg(response.message)
            } else {
                toasterrormsg(response?.message || 'Action failed')
            }
        } catch (error) {
            toasterrormsg('Something went wrong. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const confirmdelete = (postId: any, postedBy: string) => {
        Swal.fire({
            title: 'Are you sure?',
            html: `
            <p>Are you sure you want to delete this post?</p>
            <small style="color:#6c757d">
                Once deleted, this post will be permanently removed and will not be visible anywhere.
            </small>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setIsLoading(true)
                    const param = {
                        postId: postId,
                        adminId,
                        postedBy,
                    }
                    const response = await fetchData('admin/explore/post/delete', param, "DELETE")

                    if (response.status == '200') {
                        toastsuccessmsg(response.message || 'Deleted Successfully')
                        postListAPIcall()
                    } else {
                        toasterrormsg(response.message)
                    }
                } catch (error) {
                    console.error('Error deleting post:', error)
                    toasterrormsg('Failed to delete post')
                } finally {
                    setIsLoading(false)
                }
            }
        })
    }
    const changePostType = async (postId: any, type: string, postedBy: string) => {
        
        try {
            setIsLoading(true)
            const param = {
                postId: postId,
                type: type == "USER" ? "FEATURED" : "USER",
                adminId,
                postedBy,
            }
            const response = await fetchData('admin/explore/post/set/type', param)

            if (response.status == '200') {
                toastsuccessmsg(response.message || `Post Type Changed To ${type == "USER" ? "FEATURED" : "USER"} Successfully`)
                postListAPIcall()
            } else {
                toasterrormsg(response.message)
            }
        } catch (error) {
            console.error('Error changing type:', error)
            toasterrormsg('Failed to change type')
        } finally {
            setIsLoading(false)
        }


    }

    const postSearch = (text: string, isfoc: any) => {
        setSearchTerm(text)
        setFocus(isfoc)

        setOffset(0)
    }

    const handleSort = (column: any, sortDirection: string) => {
        try {
            const fieldName = column.selector ? String(column.selector).split('.').pop() : ''
            setSortField(fieldName || '')
            setSortOrder(sortDirection === 'asc' ? 'asc' : 'desc')
        } catch (error) {
            console.error('Error in sorting:', error)
        }
    }

    const handleTabChange = (status: string) => {
        setSelectedStatus(status)
        setOffset(0)
        setSearchTerm('')
        setSortField('')
        setSortOrder('')
    }

    const CustomHeader = (props: any) => {
        const inputRef: any = useRef(null)

        // Define the onchange event handler
        const handleChange = async (e: any) => {
            // Focus on the input element when the onchange event is triggered
            await props.customSearch(e, 1)
        }
        useEffect(() => {
            // Check if the ref exists and if the component should be focused
            if (inputRef.current && props.isfoc === 1) {
                inputRef.current.focus()
            }
        }, [props.isfoc])

        return (
            <div className=' d-flex justify-content-between w-100 my-5'>
                <div className='d-flex align-items-center position-relative my-1 '>
                    <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
                    <input
                        type='text'
                        data-kt-user-table-filter='search'
                        className='form-control form-control-solid w-250px ps-14'
                        placeholder={t('Search Post')}
                        value={props.search}
                        onChange={(e: any) => handleChange(e.target.value)}
                        ref={inputRef}
                    />
                </div>

            </div>
        )
    }
    useEffect(() => {
        const initializeMenus = async () => {
            try {
                const { MenuComponent } = await import('../../../_metronic/assets/ts/components')

                setTimeout(() => {
                    MenuComponent.reinitialization()
                }, 100)
            } catch (error) {
                console.error('Error initializing menus:', error)
            }
        }

        if (postList.length > 0) {
            initializeMenus()
        }
    }, [postList])

    const column = useMemo(
        () => [
            {
                name: t('User Name'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{row.fullName || '-'}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.fullName,
            },
            // {
            //     name: t('Status'),
            //     cell: (row: any) => (
            //         <div className='my-3'>
            //             <div className='fw-bold fs-5'>{row.status || '-'}</div>
            //         </div>
            //     ),
            //     sortable: true,
            //     selector: (row: any) => row.status,
            // },
            {
                name: t('Destination Name'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{row.destinationName || '-'}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.destinationName,
            },
            {
                name: t('Country Name'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{row.countryName || '-'}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.countryName,
            },
            {
                name: t('Region Name'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{row.regionName || '-'}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.regionName,
            },
            {
                name: t('Explore Category'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{row.en_name || '-'}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.en_name,
            },
            {
                name: t('Created Date'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{formatDate(row.created, 'DD-MM-YYYY') || '-'}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.created,
            },
           ...(selectedStatus === "APPROVE"
            ? [{
                name: t('Is Featured ?'),
                cell: (row: any) => (
                    <div className='my-3'>
                        {menuIdEdit && selectedStatus == "APPROVE"  ? (
                            <label className='form-check form-switch form-check-custom form-check-success form-check-solid '>
                                <input
                                    className='form-check-input cursor-pointer'
                                    type='checkbox'
                                    value='1'
                                    title={row.type == "FEATURED" ? 'Post Is Featured' : 'Not Featured'}
                                    checked={row.type == "FEATURED" ? true : false}
                                    onChange={() => changePostType(row.postId, row.type, row.fullName || '')}
                                />
                            </label>
                        ) : (
                            ''
                        )}
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.created,
                width:"150px"
              }]
            : []),
            {
                name: t('Action'),
                cell: (row: any) => (
                    <div className='d-flex gap-2'>
                        {menuIdView && (
                            <Link
                                to={getEncryptedRoute('../explore/user/', {
                                    menuId: menuId,
                                    userId: row.userId,
                                    postId: row.postId,
                                })}
                                className='btn btn-sm btn-icon btn-light-primary'
                                title='View'
                            >
                                <KTIcon iconName='eye' className='fs-2' />
                            </Link>
                        )}

                        {menuIdEdit && selectedStatus == 'PENDING' && (
                            <>
                                <button
                                    onClick={() => approveRejectPost('APPROVE', row.postId, row.fullName || '')}
                                    className='btn btn-sm btn-icon btn-light-success'
                                    title='Approve Post'
                                >
                                    <KTIcon iconName='check' className='fs-2' />
                                </button>

                                <button
                                    onClick={() => approveRejectPost('REJECT', row.postId, row.fullName || '')}
                                    className='btn btn-sm btn-icon btn-light-danger'
                                    title='Reject Post'
                                >
                                    <KTIcon iconName='cross' className='fs-2' />
                                </button>
                            </>
                        )}

                        {menuIdDelete && selectedStatus == 'APPROVE' && (
                            <button
                                onClick={() => confirmdelete(row.postId || row.id, row.fullName || '')}
                                className='btn btn-sm btn-icon btn-light-danger'
                                title='Delete'
                            >
                                <KTIcon iconName='trash' className='fs-2' />
                            </button>
                        )}

                        {menuIdEdit && selectedStatus == "APPROVE" && (
                            row.reported ? (
                                <span className='badge badge-light-danger px-3 py-2 fs-8 fw-bold'>{t('Reported')}</span>
                            ) : (
                                <div className='me-n2'>
                                    <a
                                        href='#'
                                        className='btn btn-sm btn-icon btn-light-danger'
                                        data-kt-menu-trigger='click'
                                        data-kt-menu-placement='bottom-start'
                                        data-kt-menu-overflow='false'
                                        data-kt-menu-target={`#kt_post_report_menu_${row.postId}`}
                                        title='Report Post'
                                    >
                                        <KTIcon iconName='shield-cross' className=' fs-1' />
                                    </a>
                                    <PostReportMenu postId={row.postId} />
                                </div>
                            )
                        )}
                    </div>
                ),
                width: '210px',
            },
        ],
        [t, menuIdView, menuIdEdit, menuIdDelete, menuId, selectedStatus]
    )

    useEffect(() => {
        postListAPIcall()
    }, [limit, offset, selectedStatus, sortField, sortOrder, searchTerm])

    useEffect(() => {
        document.title = 'Insayr | Post Management'
    }, [])

    const handlePageChange = (newPage: number) => {
        try {
            const totalPages = Math.ceil(totalRecords / limit)

            if (newPage === 1) {
                setOffset(0)
            } else if (newPage === totalPages) {
                setOffset((totalPages - 1) * limit)
            } else {
                setOffset((newPage - 1) * limit)
            }
        } catch (error) {
            console.error('Error in page change:', error)
        }
    }

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        try {
            setLimit(newRowsPerPage)
            setOffset(0)
        } catch (error) {
            console.error('Error in rows per page change:', error)
        }
    }

    return (
        <>
            <div className='card mb-5 mb-xl-10'>
                <div className='card-body'>
                    <div className='d-flex overflow-auto'>
                        <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder my-2 flex-nowrap'>
                            <li className='nav-item'>
                                <button
                                    className={`nav-link text-active-primary me-6 p-3 text-nowrap ${selectedStatus == 'PENDING' ? 'bg-success text-white rounded' : 'nav-link text-active-primary me-6 p-3 text-nowrap bg-primary text-white rounded'
                                        }`}
                                    onClick={() => handleTabChange('PENDING')}
                                >
                                    {t('Pending')}
                                </button>
                            </li>

                            <li className='nav-item'>
                                <button
                                    className={`nav-link text-active-primary me-6 p-3 text-nowrap ${selectedStatus == 'APPROVE' ? 'bg-success text-white rounded' : 'nav-link text-active-primary me-6 p-3 text-nowrap bg-primary text-white rounded'
                                        }`}
                                    onClick={() => handleTabChange('APPROVE')}
                                >
                                    {t('Approved')}
                                </button>
                            </li>
                            <li className='nav-item'>
                                <button
                                    className={`nav-link text-active-primary me-6 p-3 text-nowrap ${selectedStatus == 'REJECT' ? 'bg-success text-white rounded' : 'nav-link text-active-primary me-6 p-3 text-nowrap bg-primary text-white rounded'
                                        }`}
                                    onClick={() => handleTabChange('REJECT')}
                                >
                                    {t('Rejected')}
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <ToastContainer />
            {isLoading && <Loader />}

            <DataTable
                columns={column}
                data={postList}
                pagination
                paginationServer={true}
                onChangePage={handlePageChange}
                paginationTotalRows={totalRecords}
                paginationPerPage={limit}
                onChangeRowsPerPage={handleRowsPerPageChange}
                paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
                sortServer={true}
                onSort={handleSort}
                subHeader={true}
                subHeaderComponent={<CustomHeader search={searchTerm} isfoc={focus} customSearch={postSearch} />}
                paginationComponentOptions={{
                    rowsPerPageText: t('Rows Per Page'),
                }}
            />
        </>
    )
}

export { PostManagement }